import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { EllipsisVertical } from 'lucide-react';
import { useCallback, useState } from 'react';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { Button as ShadcnButton } from '@repo/components';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@repo/components';
import { Spinner } from '@repo/components';
import { useUserPreferences } from 'src/contexts/UserPreferenceContext';
import { VisualSettings } from '@repo/shared/domain';
import ActionsDrawer from 'src/parts/overview/ActionsDrawer';
import { getSite, getRemoteActions, hasReviewsAndApprovals } from 'src/api/admin/sites';

export const Route = createFileRoute('/_admin/$siteId/overview')({
	loader: async ({ params }) => {
		const { siteId } = params;
		const [site, remoteActions, hasReviewsAndApprovalsResult] = await Promise.all([
			getSite(siteId),
			getRemoteActions(siteId),
			hasReviewsAndApprovals(siteId),
		]);
		return { site, remoteActions, hasReviewsAndApprovals: hasReviewsAndApprovalsResult };
	},
	component: OverviewLayout,
});

function OverviewLayout() {
	const { site, remoteActions, hasReviewsAndApprovals: hasReviewsAndApprovalsData } = Route.useLoaderData();
	const navigate = useNavigate();
	const location = useLocation();
	const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);
	const { userPreferences, loading, updateUserPreferences } = useUserPreferences();

	const isOverviewPage = location.pathname === `/${site.id}/overview`;

	const isMobile = useMediaQuery('(max-width:600px)');
	const shouldRenderDrawer = !!remoteActions?.length;

	const handleVisualToggle = (key: keyof VisualSettings, value: boolean) => {
		const updatedVisualSettings = Object.assign({}, userPreferences?.visualSettings, { [key]: value });
		updateUserPreferences('visualSettings', updatedVisualSettings);
	};

	const visualOptions = [
		{ key: 'userMetrics' as keyof VisualSettings, label: 'User Metrics' },
		{
			key: 'dailyUsersChart' as keyof VisualSettings,
			label: 'Daily Users Chart',
		},
		{
			key: 'complianceSection' as keyof VisualSettings,
			label: 'Compliance Section',
		},
		{
			key: 'eventOverviewSection' as keyof VisualSettings,
			label: 'Event Overview',
		},
		{
			key: 'inhoudingsplicht' as keyof VisualSettings,
			label: 'Inhoudingsplicht',
		},
	];

	const tabs = [
		{
			id: '0',
			label: 'Overview',
		},
		{
			id: '2',
			label: 'Approvals',
			error: hasReviewsAndApprovalsData.hasApprovals ? '•' : undefined,
		},
		{
			id: '3',
			label: 'To Review',
			error: hasReviewsAndApprovalsData.hasReviews ? '•' : undefined,
		},
		{
			id: '1',
			label: 'Attendance',
		},
	];

	const handleTabChange = useCallback(
		(tabIndex: string) => {
			switch (tabIndex) {
				case '0':
					navigate({ to: '/$siteId/overview', params: { siteId: site.id } });
					break;
				case '1':
					navigate({ to: '/$siteId/overview/attendance', params: { siteId: site.id } });
					break;
				case '2':
					navigate({ to: '/$siteId/overview/approvals', params: { siteId: site.id } });
					break;
				case '3':
					navigate({ to: '/$siteId/overview/to-review', params: { siteId: site.id } });
					break;
			}
		},
		[navigate, site.id],
	);

	const urlAccessor = () => {
		if (location.pathname.includes('attendance')) {
			return '1';
		}
		if (location.pathname.includes('approvals')) {
			return '2';
		}
		if (location.pathname.includes('to-review')) {
			return '3';
		}

		return '0';
	};

	const childrenWidth = isMobile ? 'w-0' : isSideDrawerOpen ? 'w-[calc(100%-var(--side-drawer-width))]' : 'w-full';

	const headerActions = isOverviewPage
		? [
				<DropdownMenu key="customize-visuals">
					<DropdownMenuTrigger asChild>
						<ShadcnButton size="lg-icon-in-button" variant="outline-bg-white">
							<EllipsisVertical />
						</ShadcnButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-[160px]">
						{visualOptions.map(({ key, label }) => (
							<DropdownMenuCheckboxItem
								key={key}
								checked={userPreferences?.visualSettings?.[key] ?? false}
								onCheckedChange={(value) => handleVisualToggle(key, value)}
							>
								{label}
							</DropdownMenuCheckboxItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>,
			]
		: undefined;

	if (loading) {
		return (
			<div className="h-full w-full flex items-center justify-center">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<GenericDataPageHeader tabs={tabs} onTabChange={handleTabChange} urlAccessor={urlAccessor} actions={headerActions} />
			<div className={`transition-all duration-300 ${childrenWidth}`}>
				<Outlet />
			</div>
			{shouldRenderDrawer && (
				<>
					<Button
						variant="contained"
						color="primary"
						sx={{
							position: 'fixed',
							top: 72,
							minWidth: 10,
							width: 10,
							fontSize: 12,
							padding: 1.5,
							right: isSideDrawerOpen ? 'var(--side-drawer-width)' : 0,
							borderTopRightRadius: 0,
							borderBottomRightRadius: 0,
							writingMode: 'vertical-rl',
							transition: '0.2s',
							':hover': {
								color: 'grey',
							},
						}}
						onClick={() => {
							setIsSideDrawerOpen((previousIsSideDrawerOpen) => !previousIsSideDrawerOpen);
						}}
					>
						Remote Actions
					</Button>
					<Drawer
						variant={isMobile ? 'temporary' : 'persistent'}
						anchor="right"
						open={!!isSideDrawerOpen}
						onClose={() => {
							setIsSideDrawerOpen(false);
						}}
						sx={{
							zIndex: 1000,
							'& .MuiPaper-root': {
								overflowY: 'visible',
								maxWidth: '80%',
							},
						}}
					>
						<div style={{ width: 'var(--side-drawer-width)' }} className="overflow-y-auto">
							<ActionsDrawer data={remoteActions} />
						</div>
					</Drawer>
				</>
			)}
		</>
	);
}
